import { useEffect, useRef, useState } from "react";

import {
    useTheme,
    useMediaQuery,
    Button,
    Modal,
    Grid,
    Divider,
    Link,
    Snackbar,
    Alert,
    CircularProgress,
    ToggleButton,
    ToggleButtonGroup,
} from "@mui/material";
import { Download, AltArrowLeft } from "@solar-icons/react/ssr";

import { request } from "@/Api";
import { TopSheetPreview } from "@/sheet/TopSheetPreview";
import {
    AllGroupAccordions,
    SwitchItem,
    SignerPopup,
    InfoInput,
    SendersInput,
} from "@/sheet/TopSheetConfiguratorComponents";
import TopSheetConfiguratorLegalEntity from "@/sheet/TopSheetConfiguratorLegalEntity";
import { downloadTopsheet } from "@/sheet/TopsheetDownload";
import { BID_STATUS_APPROVED } from "@/sheet/constants";
import { PreviewContainer } from "@/sheet/TopSheetPreviewComponents";
import { getReadOnly, store } from "@/sheet/bidStore";
import useEntitlements from "@/auth/useEntitlements";
import { FEATURE_BID_XODOSIGN, FEATURE_MULTI_PERSON } from "@/constants";

export function TopSheetConfigurator({
    bid: paramBid,
    requestFormOpen,
    setRequestFormOpen,
    open,
    close,
}) {
    const bidBeforeConfig = useRef();
    const { hasFeature } = useEntitlements();
    const theme = useTheme();
    const isSmall = useMediaQuery(theme.breakpoints.down("md"));

    const [isResetting, setIsResetting] = useState(false);
    const [signatureRequested, setSignatureRequested] = useState(false);
    const [disclaimers, setDisclaimers] = useState([]);

    const readOnly = getReadOnly();
    const activeBid = paramBid || store.bid;
    const bid = activeBid;
    const project = activeBid.project;

    const closeRequestForm = () => {
        setRequestFormOpen(false);
    };

    const onInfoBlur = (field) => (event) => {
        if (bid.top_sheet_extra_info == event.target.value) {
            return;
        }
        updateTopSheet({ ...bid, [field]: event.target.value });
    };

    const onSendersChange = (event, value) => {
        const top_sheet_sender_ids = value.map((sender) => sender.id);
        updateTopSheet({
            ...bid,
            top_sheet_senders: value,
            top_sheet_sender_ids: top_sheet_sender_ids,
        });
    };

    const onSwitchChange = (field) => (_, value) => {
        updateTopSheet({ [field]: value });
    };

    const onToggleButtonChange = (field) => (_, value) => {
        if (value == null) {
            return;
        }
        updateTopSheet({ [field]: value });
    };

    const resetTopSheet = () => {
        setIsResetting(true);
        updateTopSheet(bidBeforeConfig.current);
    };

    const updateTopSheet = (bidData) => {
        request.patch(`/bids/${bid.id}/topsheet`, bidData).then(() => {
            setIsResetting(false);
        });
    };

    const onDisclaimerChange = (e, checked) => {
        const disclaimerId = Number(e.target.value);
        const disclaimeIds = checked
            ? [...bid.disclaimer_ids, disclaimerId]
            : bid.disclaimer_ids.filter((id) => id != disclaimerId);
        updateTopSheet({ disclaimer_ids: disclaimeIds });
    };

    useEffect(() => {
        if (!bid.id) {
            return;
        }
        bidBeforeConfig.current = { ...bid };
        bidBeforeConfig.current.top_sheet_sender_ids =
            bid.top_sheet_senders.map((sender) => {
                return sender.id;
            });
    }, [open]);

    useEffect(() => {
        request.get(`/bid-disclaimers`).then((response) => {
            setDisclaimers(response.data);
        });
    }, []);

    if (!bid.id) {
        return;
    }

    return (
        <Modal open={open} onClose={close}>
            <div style={{ display: "flex", height: "100%" }}>
                <SignerPopup
                    bidId={bid.id}
                    open={requestFormOpen}
                    close={closeRequestForm}
                    setSignatureRequested={setSignatureRequested}
                />
                <Snackbar
                    open={signatureRequested}
                    autoHideDuration={5000}
                    onClose={(event, reason) => {
                        setSignatureRequested(false);
                    }}
                >
                    <Alert severity="success">
                        Signature request successful!
                    </Alert>
                </Snackbar>

                <div
                    style={{
                        display: "flex",
                        width: "100%",
                        marginRight: 20,
                        flexDirection: "column",
                        margin: 1,
                    }}
                >
                    <div
                        hidden={!isSmall}
                        style={{ padding: "20px 20px 0px 20px" }}
                    >
                        <Grid
                            container
                            justifyContent={"space-between"}
                            textAlign={"center"}
                        >
                            <Grid item>
                                <Link
                                    sx={{
                                        cursor: "pointer",
                                        color: theme.palette.secondary.main,
                                        textDecoration: "none",
                                    }}
                                    onClick={close}
                                >
                                    <span
                                        style={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 5,
                                        }}
                                    >
                                        <AltArrowLeft size={18} />
                                        Back to Bid
                                    </span>
                                </Link>
                            </Grid>
                            <Grid item>
                                <Link
                                    sx={{
                                        cursor: "pointer",
                                        color: theme.palette.primary.main,
                                        textDecoration: "none",
                                    }}
                                    onClick={() => {
                                        downloadTopsheet(bid.name);
                                    }}
                                >
                                    <span
                                        style={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 5,
                                        }}
                                    >
                                        <Download size={18} />
                                        Download PDF
                                    </span>
                                </Link>
                            </Grid>
                        </Grid>
                    </div>
                    <TopSheetPreview bid={paramBid} />
                </div>

                <div
                    hidden={isSmall}
                    style={{
                        background: theme.palette.background.default,
                        width: isSmall ? "100%" : "600px",
                        padding: "25px 40px 0px 40px",
                        overflowY: "scroll",
                    }}
                >
                    {project && (
                        <>
                            <p>{project.name}</p>
                            <p style={{ fontSize: 20, fontWeight: 600 }}>
                                Top Sheet Configurator
                            </p>

                            {hasFeature(FEATURE_MULTI_PERSON) && (
                                <>
                                    <h4>Producers</h4>
                                    <SendersInput
                                        disabled={readOnly}
                                        onChange={onSendersChange}
                                        projectId={project.id}
                                        value={bid.top_sheet_senders}
                                    />
                                    <Divider sx={{ marginBottom: 4 }} />
                                </>
                            )}

                            <h4>Company Info</h4>
                            <TopSheetConfiguratorLegalEntity
                                legalEntity={project.legal_entity}
                                disabled={readOnly}
                            />
                            <Divider sx={{ marginBottom: 4, marginTop: 4 }} />
                        </>
                    )}

                    <h4>Extra Header Info</h4>
                    <InfoInput
                        disabled={readOnly}
                        onBlur={onInfoBlur("top_sheet_extra_info")}
                        text={bid.top_sheet_extra_info}
                    />
                    <Divider sx={{ marginTop: 4, marginBottom: 4 }} />

                    <h4>Section & Category Settings</h4>
                    <AllGroupAccordions
                        updateTopSheet={updateTopSheet}
                        bid={bid}
                    />
                    <Divider sx={{ marginTop: 4, marginBottom: 4 }} />

                    <h4>Include</h4>
                    <SwitchItem
                        label="Notes"
                        checked={bid.include_notes}
                        onChange={onSwitchChange("include_notes")}
                    />
                    <SwitchItem
                        label="Financial Terms"
                        checked={bid.include_financial_terms}
                        onChange={onSwitchChange("include_financial_terms")}
                    />
                    <SwitchItem
                        label="Billing Schedule"
                        checked={bid.include_bill_schedule}
                        onChange={onSwitchChange("include_bill_schedule")}
                    />

                    {disclaimers.map((disclaimer) => (
                        <SwitchItem
                            key={disclaimer.id}
                            label={disclaimer.name}
                            checked={bid.disclaimer_ids.includes(disclaimer.id)}
                            onChange={onDisclaimerChange}
                            value={disclaimer.id}
                        />
                    ))}

                    <h4>General Settings</h4>
                    <SwitchItem
                        label="Show Decimals"
                        checked={bid.include_decimals}
                        onChange={onSwitchChange("include_decimals")}
                    />
                    <SwitchItem
                        label="Display Lines with Zeros"
                        checked={bid.include_zero_lines}
                        onChange={onSwitchChange("include_zero_lines")}
                    />
                    <SwitchItem
                        label="Condensed Mode"
                        checked={bid.top_sheet_condensed}
                        onChange={onSwitchChange("top_sheet_condensed")}
                    />

                    <Grid
                        container
                        alignItems={"center"}
                        justifyContent={"space-between"}
                    >
                        <Grid item>{"Signatures Required"}</Grid>
                        <Grid item>
                            <ToggleButtonGroup
                                disabled={readOnly}
                                color="primary"
                                size="small"
                                sx={{ height: 24 }}
                                value={bid.signer_count}
                                exclusive
                                onChange={onToggleButtonChange("signer_count")}
                            >
                                <ToggleButton value={0} key={0}>
                                    0
                                </ToggleButton>
                                <ToggleButton value={1} key={1}>
                                    1
                                </ToggleButton>
                                <ToggleButton value={2} key={2}>
                                    2
                                </ToggleButton>
                            </ToggleButtonGroup>
                        </Grid>
                    </Grid>

                    <Divider sx={{ marginTop: 4, marginBottom: 4 }} />

                    <div
                        style={{
                            background: theme.palette.background.default,
                            textAlign: "center",
                            position: "sticky",
                            bottom: "0",
                            padding: "20px",
                            alignItems: "center",
                            margin: "auto",
                        }}
                    >
                        <Grid container spacing={1}>
                            <Grid md={12} item>
                                <Button
                                    fullWidth
                                    variant="contained"
                                    onClick={close}
                                >
                                    {readOnly ? "Close" : "Save & Close"}
                                </Button>
                            </Grid>
                            {!readOnly && (
                                <Grid md={12} item>
                                    {isResetting ? (
                                        <CircularProgress />
                                    ) : (
                                        <Button
                                            sx={{ color: "black" }}
                                            fullWidth
                                            variant="contained"
                                            onClick={resetTopSheet}
                                            color="inherit"
                                        >
                                            Reset Changes
                                        </Button>
                                    )}
                                </Grid>
                            )}
                            {bid.status == BID_STATUS_APPROVED &&
                                hasFeature(FEATURE_BID_XODOSIGN) && (
                                    <Grid md={12} item>
                                        <Button
                                            sx={{ color: "white" }}
                                            fullWidth
                                            variant="contained"
                                            onClick={() => {
                                                setRequestFormOpen(true);
                                            }}
                                        >
                                            Request Client Signature
                                        </Button>
                                    </Grid>
                                )}
                            <Grid md={12} item>
                                <Link
                                    onClick={() => {
                                        downloadTopsheet(bid.name);
                                    }}
                                    sx={{
                                        textDecoration: "none",
                                        cursor: "pointer",
                                    }}
                                >
                                    <Grid
                                        container
                                        alignItems={"center"}
                                        justifyContent={"center"}
                                    >
                                        <Download />
                                        &nbsp;&nbsp;
                                        <span>Download PDF</span>
                                    </Grid>
                                </Link>
                            </Grid>
                        </Grid>
                    </div>
                </div>
            </div>
        </Modal>
    );
}
